import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TemplateStudioDraft } from "@/lib/template-studio/draft/templateStudioDraft";
import { resolveTemplateLayoutRepositorySelection } from "@/lib/template-studio/repository/getTemplateLayoutRepository";
import type { TemplateLayoutRepository } from "@/lib/template-studio/repository/templateLayoutRepository.types";
import { upsertCachedTemplateLayout } from "@/lib/template-studio/repository/useTemplateLayoutList";
import { duplicateTemplateDefaultName } from "@/lib/template-studio/templateStudioEntry";
import useAuth, { type AuthState } from "@/store/auth.slice";
import type { TemplateLayout } from "@/types/template-layout/templateLayout.types";
import { saveTemplateStudioDraft } from "./saveTemplateStudioDraft";
import {
  beginTemplateStudioSave,
  completeTemplateStudioSaveSuccess,
  createInitialTemplateStudioSaveState,
  createTemplateStudioSavedTemplateRecord,
  failTemplateStudioSave,
  isTemplateStudioSaveAsCopyError,
  resolveTemplateStudioSaveErrorMessage,
  resolveTemplateStudioSaveMetadata,
  syncTemplateStudioSaveDirtyState,
  TEMPLATE_STUDIO_SAVE_ERROR_MESSAGE,
  type TemplateStudioSaveMetadata,
  type TemplateStudioSaveState,
} from "./templateStudioSaveState";

export interface UseTemplateStudioSaveOptions {
  draft: TemplateStudioDraft | null;
  repository?: TemplateLayoutRepository;
  /** Edit mode: the loaded template, so Save updates it and the draft starts clean. */
  initialSavedTemplate?: TemplateLayout;
  /** Duplicate mode: Save dialog name and description used until the copy is first saved. */
  initialMetadata?: TemplateStudioSaveMetadata;
}

export interface UseTemplateStudioSaveReturn {
  saveState: TemplateStudioSaveState;
  isSaveModalOpen: boolean;
  openSaveModal: () => void;
  closeSaveModal: () => void;
  submitSave: (
    metadata: TemplateStudioSaveMetadata,
  ) => Promise<TemplateLayout | null>;
  saveModalErrorMessage: string | null;
  saveModalInitialMetadata: TemplateStudioSaveMetadata;
  /** True while the modal saves the draft as a new template instead of updating. */
  isSavingAsCopy: boolean;
  /** Set when the saved template can't be updated (conflict or in use); switches the modal to save a copy of what the user typed. */
  startSaveAsCopy: ((metadata: TemplateStudioSaveMetadata) => void) | null;
  resetSaveState: () => void;
}

/**
 * Manages Template Studio save state, modal visibility, and repository persistence.
 * @public
 */
export const useTemplateStudioSave = ({
  draft,
  repository: repositoryOverride,
  initialSavedTemplate,
  initialMetadata,
}: UseTemplateStudioSaveOptions): UseTemplateStudioSaveReturn => {
  const user = useAuth((state: AuthState) => state.user);
  const [storedSaveState, setStoredSaveState] = useState(
    (): TemplateStudioSaveState => ({
      ...createInitialTemplateStudioSaveState(),
      savedTemplate:
        initialSavedTemplate == null
          ? null
          : createTemplateStudioSavedTemplateRecord(initialSavedTemplate),
    }),
  );
  const draftRef = useRef(draft);
  const isSaveInFlightRef = useRef(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  /** The name and description typed when the user chose "Save as copy". */
  const [copyMetadata, setCopyMetadata] =
    useState<TemplateStudioSaveMetadata | null>(null);
  const isSavingAsCopy = copyMetadata != null;

  const saveState = useMemo(() => {
    const syncedState = syncTemplateStudioSaveDirtyState(
      storedSaveState,
      draft,
    );

    return draft == null
      ? syncedState
      : { ...storedSaveState, isDirty: syncedState.isDirty };
  }, [draft, storedSaveState]);

  useEffect(() => {
    draftRef.current = draft;

    setStoredSaveState((previous) => {
      if (draft == null) return createInitialTemplateStudioSaveState();
      if (previous.error == null) return previous;

      const syncedState = syncTemplateStudioSaveDirtyState(previous, draft);
      return syncedState.error === previous.error
        ? previous
        : { ...previous, error: syncedState.error };
    });
  }, [draft]);

  const openSaveModal = useCallback(() => {
    if (draft == null || saveState.isSaving) return;
    // Conflict and in-use errors stay so the reopened dialog still explains "Save as copy".
    setStoredSaveState((previous) =>
      previous.error == null || isTemplateStudioSaveAsCopyError(previous.error)
        ? previous
        : { ...previous, error: null },
    );
    setCopyMetadata(null);
    setIsSaveModalOpen(true);
  }, [draft, saveState.isSaving]);

  const closeSaveModal = useCallback(() => {
    if (saveState.isSaving) return;
    setIsSaveModalOpen(false);
    setCopyMetadata(null);
  }, [saveState.isSaving]);

  const resetSaveState = useCallback(() => {
    isSaveInFlightRef.current = false;
    setStoredSaveState(createInitialTemplateStudioSaveState());
    setIsSaveModalOpen(false);
    setCopyMetadata(null);
  }, []);

  const submitSave = useCallback(
    async (
      metadata: TemplateStudioSaveMetadata,
    ): Promise<TemplateLayout | null> => {
      if (draft == null || isSaveInFlightRef.current) {
        return null;
      }

      isSaveInFlightRef.current = true;
      const activeState = beginTemplateStudioSave(saveState);
      setStoredSaveState(activeState);

      try {
        const { repository, context } =
          resolveTemplateLayoutRepositorySelection({
            user,
            repository: repositoryOverride,
          });
        const result = await saveTemplateStudioDraft({
          repository,
          context,
          draft,
          metadata,
          savedTemplate: isSavingAsCopy ? null : activeState.savedTemplate,
        });

        if (result.success) {
          upsertCachedTemplateLayout(repository, context, result.data);
          setStoredSaveState((previous) =>
            completeTemplateStudioSaveSuccess(
              previous,
              result.data,
              draftRef.current ?? draft,
            ),
          );
          setIsSaveModalOpen(false);
          setCopyMetadata(null);
          return result.data;
        }

        setStoredSaveState(failTemplateStudioSave(activeState, result.error));
      } catch {
        setStoredSaveState(
          failTemplateStudioSave(activeState, {
            code: "REQUEST_FAILURE",
            message: TEMPLATE_STUDIO_SAVE_ERROR_MESSAGE.request,
          }),
        );
      } finally {
        isSaveInFlightRef.current = false;
      }

      return null;
    },
    [draft, isSavingAsCopy, repositoryOverride, saveState, user],
  );

  const saveModalInitialMetadata = useMemo((): TemplateStudioSaveMetadata => {
    if (copyMetadata != null) {
      return {
        name: duplicateTemplateDefaultName(copyMetadata.name),
        description: copyMetadata.description,
      };
    }
    return saveState.savedTemplate == null && initialMetadata !== undefined
      ? initialMetadata
      : resolveTemplateStudioSaveMetadata(saveState);
  }, [copyMetadata, initialMetadata, saveState]);

  // Derived so the "Save as copy" button never renders without its explanation;
  // hidden once the user switches to saving a copy.
  const saveModalErrorMessage =
    saveState.error == null ||
    (isSavingAsCopy && isTemplateStudioSaveAsCopyError(saveState.error))
      ? null
      : resolveTemplateStudioSaveErrorMessage(saveState.error);

  const canStartSaveAsCopy =
    !isSavingAsCopy &&
    !saveState.isSaving &&
    saveState.savedTemplate != null &&
    isTemplateStudioSaveAsCopyError(saveState.error);

  const startSaveAsCopy = useCallback(
    (metadata: TemplateStudioSaveMetadata) => setCopyMetadata(metadata),
    [],
  );

  return {
    saveState,
    isSaveModalOpen,
    openSaveModal,
    closeSaveModal,
    submitSave,
    saveModalErrorMessage,
    saveModalInitialMetadata,
    isSavingAsCopy,
    startSaveAsCopy: canStartSaveAsCopy ? startSaveAsCopy : null,
    resetSaveState,
  };
};
